import { Inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { AbstractAuthService, UserInfo, UserParametersService } from 'finzzle-angular-utils';
import { BehaviorSubject } from 'rxjs';
import { Consultant, ConsultantHabilitation } from '../models/generated/graphql';
import { filterHabilitations } from '../utils/habilitation';

const notifyLogin = gql`
  mutation notifyUserLogin {
    notifyUserLogin {
      notifications {
        id
        message
        isActive
        consultantId
        backOfficeMemberId
        link
        linkLabel
      }
      backOfficeMember {
        id
        tokenId
        displayName
        groupeGestionnaire
        isAdmin
        isActive
        withGroupeAccessExtended
        serviceAllowedEnvelopeIds
      }
    }
  }
`;

const deleteNotificationByLink = gql`
  mutation deleteNotificationsByLink($link: String!) {
    deleteNotificationsByLink(link: $link)
  }
`;

const consultantUser = gql`
  query consultantByCode($code: String!) {
    consultantByCode(code: $code) {
      id
      code
      gerePar
      consultantHabilitations {
        id
        habilitationId
        dateDebut
        dateFin
        consultantId
        codeHabilitation
        statut
        habilitation {
          id
          code
          libelle
        }
      }
    }
  }
`;

const CHECK_HABILIATIONS_CODES = ['ASSU', 'CIF', 'ALPSI'];

// Class handling the login process, token decoding, and user information retrieval
@Injectable({
  providedIn: 'root',
})
export class AuthService extends AbstractAuthService {
  isBackOfficeValue = false;
  isAdminSubject = new BehaviorSubject<boolean>(false);
  public readonly userNotificationsSubject = new BehaviorSubject<Notification[]>([]);
  private userConsultantValidHabilitations: ConsultantHabilitation[] = [];

  private currentConsultantSubject = new BehaviorSubject<Consultant | null>(null);
  public readonly currentConsultant$ = this.currentConsultantSubject.asObservable();

  get currentConsultant(): Consultant | null {
    return this.currentConsultantSubject.value;
  }

  get gerePar(): Consultant['gerePar'] | null {
    return this.currentConsultantSubject.value?.gerePar ?? null;
  }

  userHabilitationCheck: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private userConsultantId: number | null = null;
  constructor(
    private apollo: Apollo,
    @Inject('environment')
    environment: any,
    userParametersService: UserParametersService
  ) {
    super(environment, userParametersService);
  }

  get consultantId(): number | null {
    return this.userConsultantId;
  }

  onNewValidToken() {
    if (!this.isBackOfficeValue) {
      this.getConsultantHabilitations();
    } else {
      this.userHabilitationCheck.next(true);
    }
  }

  isTokenValid(): boolean {
    const backOfficeGroups = this.environment?.authConfig?.backOfficeGroups ?? [];
    const consultantGroups = this.environment?.authConfig?.consultantGroups ?? [];
    const backOfficeAdminGroups = this.environment?.authConfig?.backOfficeAdminGroups ?? [];

    let isAuthent = false;

    this.isBackOfficeValue =
      this.tokenInfo?.memberOf?.filter(
        (value: string) => backOfficeGroups?.some((expectedValue: string) => value.includes(expectedValue)) ?? false
      )?.length > 0;
    let isAdminValue =
      this.tokenInfo?.memberOf?.filter(
        (value: string) =>
          backOfficeAdminGroups?.some((expectedValue: string) => value.includes(expectedValue)) ?? false
      )?.length > 0;
    this.isAdminSubject.next(isAdminValue);
    if (!this.isBackOfficeValue) {
      isAuthent =
        this.tokenInfo?.memberOf?.filter(
          (value: string) => consultantGroups?.some((expectedValue: string) => value.includes(expectedValue)) ?? false
        )?.length > 0;
    } else {
      isAuthent = true;
    }
    return isAuthent;
  }

  // Retrieves the user information extracted from the JWT token.
  getUserInfo(): UserInfo {
    return {
      givenName: this.tokenInfo?.givenName,
      lastName: this.tokenInfo?.lastName,
      fullName: this.tokenInfo?.givenName + ' ' + (this.tokenInfo?.lastName ?? this.tokenInfo?.sn ?? ''),
      email: this.tokenInfo?.sub,
      tokenId: this.tokenInfo?.id,
      consultantCode: this.tokenInfo?.consultantId,
    };
  }

  isAdminRole(): boolean {
    return this.isBackOffice() && this.isAdminSubject?.getValue();
  }
  isBackOffice(): boolean {
    return this.isBackOfficeValue;
  }

  getConsultantHabilitations() {
    this.apollo
      .query<{ consultantByCode: Consultant }>({
        query: consultantUser,
        variables: {
          code: this.tokenInfo?.id,
        },
      })
      .subscribe(result => {
        const userConsultant = result.data?.consultantByCode;
        if (!userConsultant) return;
        this.currentConsultantSubject.next(userConsultant);

        if (userConsultant && userConsultant.consultantHabilitations) {
          const habs = filterHabilitations(userConsultant.consultantHabilitations);
          this.userConsultantValidHabilitations = habs.valid;
          this.userConsultantId = userConsultant.id;
          this.userHabilitationCheck.next(true);
        }
      });
  }

  getUserValidHabilitations(): ConsultantHabilitation[] {
    return this.userConsultantValidHabilitations;
  }


  getUserValidHabilitationIds(): number[] {
    if (!this.userConsultantValidHabilitations) {
      return [];
    }
    return this.userConsultantValidHabilitations.map(hab => hab.habilitationId);
  }

  isSomeHabilitationValid(): boolean {
    return this.userConsultantValidHabilitations?.some(
      hab => hab.codeHabilitation && CHECK_HABILIATIONS_CODES.includes(hab.codeHabilitation)
    );
  }

  override onReset(): void {
    this.isBackOfficeValue = false;
    this.isAdminSubject.next(false);
    this.userConsultantValidHabilitations = [];
    this.userHabilitationCheck.next(false);
    this.currentConsultantSubject.next(null);
  }
}
